/* eslint-disable no-console */
import { Contact } from '../../../components/address-book';
import { ENVELOPES } from '../../../components/envelope-preview/constants';
import { useAppContext } from '../../../context/app-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { Address, AddressResponseData } from '../../../global-types/addresses';
import { EnvelopePreviewResponse, EnvelopeType } from '../../../global-types/envelope';
import { useFeatureFlags, useIsDigitalGreeting } from '../../../hooks';
import useAddressSelection from '../../../hooks/use-address-selection';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { getEnvelopePreview } from '../../../services';
import { useEnvelopeSelection } from '../../select-envelope/hooks/use-envelope-selection';
import { getEnvelopePreviewBody } from '../utils';

export const useLoadPreviewOnFrontend = () => {
  const { initializedDataState } = useInitializationDataContext();
  const { isUS } = initializedDataState;
  const isDigitalGreetings = useIsDigitalGreeting();
  const {
    initializedDataState: { data: initializedData, addressData },
  } = useInitializationDataContext();
  const { sender: senderSelection, contacts } = useAddressSelection();
  const { selectedEnvelope } = useEnvelopeSelection();
  const { IS_MULT_ADDRESS_ENABLED } = useFeatureFlags();
  const { appState } = useAppContext();
  const { previewImages } = appState;
  const isOneToMany = useIsOneToMany();
  const projectId = initializedData?.project_id || '';

  const assignEnvelope = async ({
    sender,
    recipient,
  }: {
    sender?: AddressResponseData | Address | Contact | null;
    recipient?: AddressResponseData | Address | Contact | null;
  }) => {
    // if no initialized data, return the existing previewImages
    if (!initializedData) return [...previewImages];

    // If we have recipient/sender, or if it's 1-to-many and we have sender, send request to get envelope preview
    const envelopePreviewIndex = previewImages.findIndex((preview) => preview.type === 'Envelope');
    if (envelopePreviewIndex > -1) {
      // If we already have an envelope preview, remove it
      previewImages.splice(envelopePreviewIndex, 1);
    }
    const envelopePreviewBody = getEnvelopePreviewBody({
      recipient,
      sender,
      color: initializedData.product.envelope_color,
      envelopeSize: initializedData.variables.template_data.CardSize,
    });
    const envResponse: EnvelopePreviewResponse = await getEnvelopePreview(projectId, envelopePreviewBody);

    return [
      ...previewImages,
      {
        dataURL: envResponse.data?.encoded_image as string,
        type: 'Envelope',
      },
    ];
  };

  const getPreviews = async () => {
    if (isUS && !isDigitalGreetings) {
      const { recipient, sender } = addressData;

      if (!recipient && !sender && !senderSelection) {
        // If we have recipient/sender, or if it's 1-to-many and we have sender, send request to get envelope preview
        const envelopePreviewIndex = previewImages.findIndex((preview) => preview.type === 'Envelope');
        if (envelopePreviewIndex > -1) {
          // If we already have an envelope preview, remove it
          previewImages.splice(envelopePreviewIndex, 1);
        }

        // If no recipient/sender, add blank envelope to previewImages (1-to-many)
        return [
          ...previewImages,
          {
            dataURL: ENVELOPES[0].image,
            type: 'Envelope',
          },
        ];
      }

      if (IS_MULT_ADDRESS_ENABLED && isOneToMany && selectedEnvelope === EnvelopeType.ReturnRecipient) {
        return await assignEnvelope({ sender: senderSelection, recipient: contacts.at(-1) ?? null });
      }

      if (isOneToMany && sender) {
        // if 1:M card, we need to assign the sender address
        // to the envelope when IS_MULT_ADDRESS_ENABLED is disabled
        return await assignEnvelope({ sender, recipient: null });
      }

      // default is handling 1:1 card with both sender and recipient
      return await assignEnvelope({ sender, recipient });
    }

    return previewImages;
  };

  return {
    getPreviews,
  };
};
